export class FilePreview {
  constructor(file, options = {}) {
    this.file = file
    this.options = {
      maxWidth: options.maxWidth || 200,
      maxHeight: options.maxHeight || 200,
      quality: options.quality || 0.8,
      ...options,
    }
  }

  generate = () => {
    return new Promise((resolve) => {
      const fileType = this.getFileType()

      if (this.isPreviewableImage()) {
        this.generateImagePreview()
          .then((dataUrl) => {
            resolve({
              type: fileType,
              dataUrl: dataUrl,
              previewable: true,
            })
          })
          .catch((error) => {
            console.warn('Image preview generation failed:', error)
            resolve(this.generateFallbackPreview(fileType))
          })
      }
      else {
        resolve(this.generateFallbackPreview(fileType))
      }
    })
  }

  isPreviewableImage = () => {
    const mimeType = this.file.type.toLowerCase()
    const fileName = this.file.name.toLowerCase()

    // Check MIME type first
    const previewableMimes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/bmp',
      'image/svg+xml',
    ]

    if (previewableMimes.includes(mimeType)) {
      return true
    }

    // Check file extension for cases where MIME type might be missing
    const extension = fileName.split('.').pop()
    const previewableExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg']

    // Explicitly exclude TIFF files
    if (['tiff', 'tif'].includes(extension)) {
      return false
    }

    return previewableExtensions.includes(extension)
  }

  generateImagePreview = () => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = (e) => {
        const img = new Image()

        img.onload = () => {
          try {
            const canvas = this.createResizedCanvas(img)
            const dataUrl = canvas.toDataURL('image/jpeg', this.options.quality)
            resolve(dataUrl)
          }
          catch (error) {
            reject(error)
          }
        }

        img.onerror = () => {
          reject(new Error('Failed to load image for preview'))
        }

        img.src = e.target.result
      }

      reader.onerror = () => {
        reject(new Error('Failed to read file for preview'))
      }

      reader.readAsDataURL(this.file)
    })
  }

  createResizedCanvas = (img) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    // Calculate new dimensions while maintaining aspect ratio
    const { width, height } = this.calculateDimensions(img.width, img.height)

    canvas.width = width
    canvas.height = height

    // Enable image smoothing for better quality
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    // Draw the resized image
    ctx.drawImage(img, 0, 0, width, height)

    return canvas
  }

  calculateDimensions = (originalWidth, originalHeight) => {
    const { maxWidth, maxHeight } = this.options

    // If image is smaller than max dimensions, keep original size
    if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
      return { width: originalWidth, height: originalHeight }
    }

    // Calculate scaling factor
    const widthRatio = maxWidth / originalWidth
    const heightRatio = maxHeight / originalHeight
    const ratio = Math.min(widthRatio, heightRatio)

    return {
      width: Math.round(originalWidth * ratio),
      height: Math.round(originalHeight * ratio),
    }
  }

  generateFallbackPreview = (fileType) => {
    return {
      type: fileType,
      dataUrl: null,
      previewable: false,
    }
  }

  getFileType = () => {
    const fileName = this.file.name.toLowerCase()
    const extension = fileName.split('.').pop()

    // Special handling for common formats
    const typeMap = {
      // Images
      'jpg': 'JPEG',
      'jpeg': 'JPEG',
      'png': 'PNG',
      'gif': 'GIF',
      'webp': 'WEBP',
      'bmp': 'BMP',
      'svg': 'SVG',
      'tiff': 'TIFF',
      'tif': 'TIFF',

      // Documents
      'pdf': 'PDF',
      'doc': 'DOC',
      'docx': 'DOCX',
      'txt': 'TXT',
      'rtf': 'RTF',

      // Spreadsheets
      'xls': 'XLS',
      'xlsx': 'XLSX',
      'csv': 'CSV',

      // Presentations
      'ppt': 'PPT',
      'pptx': 'PPTX',

      // Archives
      'zip': 'ZIP',
      'rar': 'RAR',
      '7z': '7Z',
      'tar': 'TAR',
      'gz': 'GZ',

      // Video
      'mp4': 'MP4',
      'avi': 'AVI',
      'mov': 'MOV',
      'wmv': 'WMV',
      'flv': 'FLV',
      'webm': 'WEBM',

      // Audio
      'mp3': 'MP3',
      'wav': 'WAV',
      'flac': 'FLAC',
      'aac': 'AAC',
      'ogg': 'OGG',
    }

    return typeMap[extension] || extension.toUpperCase() || 'FILE'
  }

  // Static method for quick preview generation
  static async quickPreview(file, options = {}) {
    const preview = new FilePreview(file, options)
    return await preview.generate()
  }
}
